import { rgbFromCssColor, rgbFromHex, rgbFromHsl, rgbFromHsv } from './color-conversion';
import { cleanHexValue } from './hex';

export const COLOR_OPACITY = 0.8;
export const CONTRAST_COLOR = '#ffffff';

const adjustChannel = (givenVal) => {
  const val = givenVal * 0.003921569; // 1 / 255 = 0.003921569; avoiding division
  if (val <= 0.03928) {
    return val / 12.92;
  }
  return ((val + 0.055) / 1.055) ** 2.4;
};

export const luminanceFromRgb = (r, g, b) => {
  const rAdjusted = adjustChannel(r);
  const gAdjusted = adjustChannel(g);
  const bAdjusted = adjustChannel(b);
  return 0.2126 * rAdjusted + 0.7152 * gAdjusted + 0.0722 * bAdjusted;
};

export const contrastRatioFromRL = (colorLuminance, contrastColorLuminance) => {
  // To calculate the contrast ratio, divide the luminance of the lighter color by the luminance of the darker color
  if (colorLuminance < contrastColorLuminance) {
    return (contrastColorLuminance + 0.05) / (colorLuminance + 0.05);
  }
  return (colorLuminance + 0.05) / (contrastColorLuminance + 0.05);
};

export const contrastRatioFromRgb = (
  r,
  g,
  b,
  colorOpacity = COLOR_OPACITY,
  contrastColor = CONTRAST_COLOR,
) => {
  const [contrastR, contrastG, contrastB] = rgbFromCssColor(contrastColor);
  const mixedR = colorOpacity * r + (1 - colorOpacity) * contrastR;
  const mixedG = colorOpacity * g + (1 - colorOpacity) * contrastG;
  const mixedB = colorOpacity * b + (1 - colorOpacity) * contrastB;

  // Constrain luminance to values between 0 and 1
  const colorLuminance = Math.max(Math.min(luminanceFromRgb(mixedR, mixedG, mixedB), 1), 0);
  const contrastColorLuminance = Math.max(
    Math.min(luminanceFromRgb(contrastR, contrastG, contrastB), 1),
    0,
  );

  return contrastRatioFromRL(colorLuminance, contrastColorLuminance);
};

export const contrastRatioFromHsl = (
  h,
  s,
  l,
  colorOpacity = COLOR_OPACITY,
  colorBackground = CONTRAST_COLOR,
) => {
  const [r, g, b] = rgbFromHsl(h, s, l);
  return contrastRatioFromRgb(r, g, b, colorOpacity, colorBackground);
};

export const contrastRatioFromHsv = (
  h,
  s,
  v,
  colorOpacity = COLOR_OPACITY,
  colorBackground = CONTRAST_COLOR,
) => {
  const [r, g, b] = rgbFromHsv(h, s, v);
  return contrastRatioFromRgb(r, g, b, colorOpacity, colorBackground);
};

export const contrastRatioFromHex = (foregroundColor, backgroundColor = CONTRAST_COLOR) => {
  const foregroundColorAsRgb = rgbFromHex(cleanHexValue(foregroundColor));
  return contrastRatioFromRgb(...foregroundColorAsRgb, 1, backgroundColor);
};
